import { AfterContentChecked, ChangeDetectorRef, Component } from '@angular/core';
import { Router } from '@angular/router';
import { NotesSecureAction, SepoDevice, ServicePointFeatures } from '@deprecated/api-interfaces';
import { Observable } from 'rxjs';
import { AuthService } from '../../../auth/service/auth.service';
import { DeviceService } from '../../../shared/services/device.service';
import { InteractionService } from '../../../shared/services/interaction.service';
import { TenantConfigService } from '../../../shared/services/tenant-config.service';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  standalone: false,
})
export class NavigationComponent implements AfterContentChecked {
  // template variables
  public device$: Observable<SepoDevice>;
  public servicePointFeatures = ServicePointFeatures;
  NotesSecureAction = NotesSecureAction;

  constructor(
    private deviceService: DeviceService,
    private auth: AuthService,
    private router: Router,
    private tenantConfigService: TenantConfigService,
    private cdr: ChangeDetectorRef,
    private interactionService: InteractionService,
  ) {
    this.device$ = this.deviceService.deviceChanged;
  }

  ngAfterContentChecked(): void {
    this.cdr.detectChanges();
  }

  public isLoggedIn(): boolean {
    return this.auth.isLoggedIn() && this.router.url !== '/login';
  }

  public logout() {
    this.auth.logout();
  }

  public isFeatureEnabled(feature: ServicePointFeatures): boolean {
    return !feature || this.tenantConfigService.isFeatureEnabled(feature);
  }

  public navigateWithDevice(path: string, feature?: ServicePointFeatures): void {
    const deviceId = this.deviceService.getDeviceId();
    if (deviceId && feature) {
      this.interactionService.checkFeatureEvent(feature, deviceId);
      this.router.navigate([`${path}/${deviceId}`]);
    } else if (deviceId) {
      this.router.navigate([`${path}/${deviceId}`]);
    } else {
      this.router.navigate([path]);
    }
  }

  public navigateToSerialNumberSearch(): void {
    this.router.navigate(['/serial']);
  }

  public navigateTo(path: string): void {
    const deviceId = this.deviceService.getDeviceId();
    if (deviceId) {
      this.interactionService.checkFeatureEvent(ServicePointFeatures.VISIT_E_SHOP_TILE, deviceId);
      this.router.navigate([`${path}/${deviceId}`]);
    }
  }

  public isMarketingTileEnabled(): boolean {
    return !this.tenantConfigService?.getValueFromServicePointFeatureWithDefaultValue('uploadCustomTileImageEnabled', false);
  }

  getMarketingTileTitle(): string {
    return this.tenantConfigService?.getValueFromServicePointFeatureWithDefaultValue('marketingTileText', '');
  }

  getMarketingTileIcon(): string {
    return this.tenantConfigService?.getValueFromServicePointFeatureWithDefaultValue('marketingTileIconName', '');
  }

  navigateToMarketing(): void {
    const url = this.tenantConfigService?.getValueFromServicePointFeatureWithDefaultValue('marketingTileURL', '');
    const deviceId = this.deviceService.getDeviceId();
    if (url) {
      if (deviceId) {
        this.interactionService.checkFeatureEvent(ServicePointFeatures.MARKETING_TILE, deviceId);
      }
      window.open(url, '_blank');
    }
  }

  getPartsTileTitle(): string {
    return this.tenantConfigService.getPartsTileTitle();
  }

  getPartsTileIcon(): string {
    return this.tenantConfigService.getValueFromServicePointFeatureWithDefaultValue('sparePartsTileIconName', 'build');
  }

  getPartsTileFontSet(icon: string): string | undefined {
    return icon === 'package_2' || icon === 'service_toolbox' ? 'material-symbols-outlined' : undefined;
  }

  getVisiteShopTitle(): string {
    const customTextEnabled = this.tenantConfigService.getValueFromServicePointFeatureWithDefaultValue(
      'visiteShopCustomTextEnabled',
      false,
    );
    const customText = this.tenantConfigService.getValueFromServicePointFeatureWithDefaultValue('visiteShopCustomText', '');
    return customTextEnabled && customText ? customText : 'SERVICEPOINT.NAVIGATION.ITEMS.VISIT_E_SHOP';
  }

  getVisiteShopIcon(): string {
    const customIconEnabled = this.tenantConfigService.getValueFromServicePointFeatureWithDefaultValue(
      'visiteShopCustomIconEnabled',
      false,
    );
    const iconName = this.tenantConfigService.getValueFromServicePointFeatureWithDefaultValue('visiteShopIconName', '');
    return customIconEnabled && iconName ? iconName : 'shopping_cart';
  }
}
